import { Hourglass, Receipt, Wallet } from "lucide-mui";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  api,
  type ContactSpendLimitRow,
  type ContactUsageGroupBy,
} from "../../api/client.ts";
import NoSelectionPrompt from "../../components/NoSelectionPrompt.tsx";
import { keys } from "../../api/queryKeys.ts";
import { userHasOrgScope } from "../../auth/scopes";
import FilterBar from "../../components/filters/FilterBar.tsx";
import { currentMonth, monthFilterSpec, monthLabel } from "../../components/filters/monthSpec.tsx";
import type { FilterSpec } from "../../components/filters/specs.ts";
import { useFilters, useLocalFilterState } from "../../components/filters/useFilters.ts";
import FormattedDate from "../../components/FormattedDate";
import CenteredSpinner from "../../components/CenteredSpinner.tsx";
import { UserCell } from "../../components/IdentityCell.tsx";
import KpiTile from "../../components/KpiTile.tsx";
import Panel from "../../components/Panel.tsx";
import StatusChip from "../../components/StatusChip.tsx";
import ScrollTable from "../../components/table/ScrollTable.tsx";
import { useAuth } from "../../contexts/AuthContext.tsx";
import { useOrganization } from "../../contexts/OrganizationContext.tsx";
import { formatMoney, formatNumber, humanize } from "../../utils/format.ts";
import PageHeader from "../../components/PageHeader.tsx";

const GROUP_BY_OPTIONS: { value: ContactUsageGroupBy; label: string }[] = [
  { value: "tenant", label: "Client" },
  { value: "user", label: "User" },
  { value: "data_point", label: "Data point" },
  { value: "provider", label: "Provider" },
];

const GROUP_COLUMN: Partial<Record<ContactUsageGroupBy, string>> = {
  tenant: "Client",
  user: "User",
  provider: "Provider",
};

// The month and grouping of the usage table; this month by client until changed.
function useUsageFilterSpecs(): FilterSpec[] {
  return useMemo(
    () => [
      monthFilterSpec(),
      {
        kind: "options",
        key: "group_by",
        label: "Group by",
        param: "group_by",
        multiple: false,
        required: true,
        pinned: true,
        options: GROUP_BY_OPTIONS,
        default: ["tenant"],
      },
    ],
    [],
  );
}

function useSpendLimits(orgId: string) {
  return useQuery({
    queryKey: keys.contactEnrichmentBilling.limits(orgId),
    queryFn: () => api.getContactSpendLimits(orgId),
  });
}

function LimitRow({
  orgId,
  row,
  label,
  currency,
}: {
  orgId: string;
  row: ContactSpendLimitRow;
  label: string;
  currency: string;
}) {
  const queryClient = useQueryClient();
  const [value, setValue] = useState<string | null>(null);
  const current = value ?? (row.monthly_limit != null ? String(row.monthly_limit) : "");

  const save = useMutation({
    mutationFn: () => {
      const trimmed = current.trim();
      return api.setContactSpendLimit(orgId, row.tenant_id, trimmed === "" ? null : Number(trimmed));
    },
    onSuccess: (limits) => {
      setValue(null);
      queryClient.setQueryData(keys.contactEnrichmentBilling.limits(orgId), limits);
      toast.success(`${label} limit updated`);
    },
  });

  return (
    <TableRow>
      <TableCell>{label}</TableCell>
      <TableCell align="right">{formatMoney(row.spent, currency)}</TableCell>
      <TableCell align="right">{formatMoney(row.reserved, currency)}</TableCell>
      <TableCell align="right">
        {row.remaining != null ? formatMoney(row.remaining, currency) : "—"}
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <TextField
            size="small"
            type="number"
            value={current}
            onChange={(e) => setValue(e.target.value)}
            placeholder={row.tenant_id ? "Org limit only" : "No buying"}
            sx={{ width: 140 }}
          />
          <Button
            size="small"
            onClick={() => save.mutate()}
            disabled={value === null}
            loading={save.isPending}
          >
            Save
          </Button>
        </Stack>
      </TableCell>
    </TableRow>
  );
}

/** This month's spend for the whole organization, as headline numbers. */
function SpendKpis({ orgId }: { orgId: string }) {
  const limits = useSpendLimits(orgId);
  if (!limits.data) return null;
  const { organization, currency, month } = limits.data;
  const left =
    organization.remaining != null
      ? formatMoney(organization.remaining, currency)
      : organization.monthly_limit == null
        ? "No buying"
        : "—";

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 4 }}>
        <KpiTile
          dense
          label="Spent"
          icon={<Receipt />}
          value={formatMoney(organization.spent, currency)}
          caption={`Delivered data points in ${month}`}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <KpiTile
          dense
          label="Reserved"
          icon={<Hourglass />}
          value={formatMoney(organization.reserved, currency)}
          caption="Held for purchases still running"
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <KpiTile
          dense
          label="Left this month"
          icon={<Wallet />}
          color={organization.monthly_limit == null ? "warning" : "success"}
          value={left}
          caption={
            organization.monthly_limit != null
              ? `Of a ${formatMoney(organization.monthly_limit, currency)} organization limit`
              : "No organization limit is set"
          }
        />
      </Grid>
    </Grid>
  );
}

function LimitsCard({ orgId }: { orgId: string }) {
  const limits = useSpendLimits(orgId);
  if (limits.isLoading) return <CenteredSpinner />;
  if (!limits.data) return null;
  const { organization, tenants, currency, month } = limits.data;

  return (
    <Panel
      title={
        <>
          Monthly spend limits
          <Chip size="small" label={month} sx={{ ml: 1, verticalAlign: "middle" }} />
        </>
      }
      subtitle={
        <>
          The most this organization may spend on contact data from paid providers per calendar
          month (UTC). Each purchase reserves its worst-case cost up front, so a limit is never
          exceeded. A client limit narrows the organization limit for that client's collections.
          Partner providers are free and need no limit.
        </>
      }
      disablePadding
    >
      {organization.monthly_limit == null && (
        <Box sx={{ px: 2, pt: 2 }}>
          <Alert severity="warning">
            No organization limit is set, so nobody in this organization can buy contact data from
            paid providers. Free partner providers still work.
          </Alert>
        </Box>
      )}
      <ScrollTable>
        <TableHead>
          <TableRow>
            <TableCell>Scope</TableCell>
            <TableCell align="right">Spent</TableCell>
            <TableCell align="right">Reserved</TableCell>
            <TableCell align="right">Left</TableCell>
            <TableCell>Monthly limit ({currency})</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <LimitRow orgId={orgId} row={organization} label="Organization" currency={currency} />
          {tenants.map((t) => (
            <LimitRow
              key={t.tenant_id}
              orgId={orgId}
              row={t}
              label={t.tenant_name ?? "Client"}
              currency={currency}
            />
          ))}
        </TableBody>
      </ScrollTable>
    </Panel>
  );
}

function UsageCard({ orgId }: { orgId: string }) {
  const specs = useUsageFilterSpecs();
  const filterState = useLocalFilterState();
  const filters = useFilters(specs, { state: filterState });
  const month = filters.get("month") ?? currentMonth();
  const groupBy = (filters.get("group_by") ?? "tenant") as ContactUsageGroupBy;
  const usage = useQuery({
    queryKey: keys.contactEnrichmentBilling.usage(orgId, month, groupBy),
    queryFn: () => api.getContactUsage(orgId, month, groupBy),
  });
  const download = useMutation({
    mutationFn: () => api.downloadContactUsageCsv(orgId, month),
  });
  const currency = usage.data?.currency ?? "USD";
  const hasRows = !!usage.data && usage.data.rows.length > 0;

  return (
    <Panel
      title="Usage"
      subtitle={
        <>
          Every data point delivered, at the price in force when it was bought; a free partner
          provider's at 0. The CSV lists each charge and is what invoices are built from.
        </>
      }
      disablePadding
    >
      <FilterBar
        filters={filters}
        actions={
          <Button variant="outlined" onClick={() => download.mutate()} loading={download.isPending}>
            Export CSV
          </Button>
        }
      />
      {usage.isLoading ? (
        <CenteredSpinner />
      ) : hasRows ? (
        <ScrollTable>
          <TableHead>
            <TableRow>
              {groupBy !== "data_point" && (
                <TableCell>{GROUP_COLUMN[groupBy]}</TableCell>
              )}
              <TableCell>Data point</TableCell>
              <TableCell align="right">Quantity</TableCell>
              <TableCell align="right">Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {usage.data!.rows.map((r) => (
              <TableRow key={`${r.key}-${r.data_point}`}>
                {groupBy !== "data_point" && <TableCell>{r.label}</TableCell>}
                <TableCell>{groupBy === "data_point" ? r.label : humanize(r.data_point)}</TableCell>
                <TableCell align="right">{formatNumber(r.quantity)}</TableCell>
                <TableCell align="right">{formatMoney(r.amount, currency)}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={groupBy === "data_point" ? 2 : 3} sx={{ fontWeight: 700 }}>
                Total
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                {formatMoney(usage.data!.total, currency)}
              </TableCell>
            </TableRow>
          </TableBody>
        </ScrollTable>
      ) : (
        <Typography variant="body2" sx={{ color: "text.secondary", p: 2 }}>
          Nothing bought in {monthLabel(month)}
        </Typography>
      )}
    </Panel>
  );
}

function PurchasesCard({ orgId }: { orgId: string }) {
  const jobs = useQuery({
    queryKey: keys.contactEnrichmentJob.byOrg(orgId),
    queryFn: () => api.listContactPurchases(orgId, { limit: 50 }),
  });
  if (jobs.isLoading) return <CenteredSpinner />;
  if (!jobs.data?.length) return null;
  return (
    <Panel title="Recent purchases" count={jobs.data.length} disablePadding>
      <ScrollTable>
        <TableHead>
          <TableRow>
            <TableCell>When</TableCell>
            <TableCell>Company</TableCell>
            <TableCell>Provider</TableCell>
            <TableCell>By</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">People</TableCell>
            <TableCell align="right">Charged</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {jobs.data.map((j) => (
            <TableRow key={j.id}>
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                <FormattedDate value={j.created_at} format="datetime" />
              </TableCell>
              <TableCell>{j.company_name ?? "—"}</TableCell>
              <TableCell>{j.provider_name || j.provider}</TableCell>
              <TableCell>
                {j.user_email ? <UserCell displayName={null} email={j.user_email} /> : "—"}
              </TableCell>
              <TableCell>
                {j.error ? (
                  <Tooltip title={j.error}>
                    <span>
                      <StatusChip kind="job" value={j.status} />
                    </span>
                  </Tooltip>
                ) : (
                  <StatusChip kind="job" value={j.status} />
                )}
              </TableCell>
              <TableCell align="right">
                {j.kind === "company_lookup"
                  ? "Company lookup"
                  : `${formatNumber(j.found_count)} / ${formatNumber(j.limit)}`}
              </TableCell>
              <TableCell align="right">
                {j.billed ? (
                  formatMoney(j.charged_amount, j.currency)
                ) : (
                  <Typography component="span" variant="body2" sx={{ color: "text.secondary" }}>
                    Included
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </ScrollTable>
    </Panel>
  );
}

/** Spend limits, usage and recent purchases of contact data for one organization. */
export default function ContactDataPage() {
  const { user } = useAuth();
  const { selectedOrgId, currentOrg } = useOrganization();
  const canManage = userHasOrgScope(user, selectedOrgId, "enrichment:manage");

  if (!selectedOrgId) {
    return (
      <>
        <PageHeader title="Contact data" />
        <NoSelectionPrompt kind="org" />
      </>
    );
  }
  const orgName = currentOrg?.name ?? "this organization";
  return (
    <>
      <PageHeader
        title="Contact data"
        description={`Contacts bought from data providers for ${orgName}'s collections, with the limits that bound spend on paid providers; partner providers are free`}
      />
      {canManage ? (
        <Stack spacing={3}>
          <SpendKpis orgId={selectedOrgId} />
          <LimitsCard orgId={selectedOrgId} />
          <UsageCard orgId={selectedOrgId} />
          <PurchasesCard orgId={selectedOrgId} />
        </Stack>
      ) : (
        <Alert severity="info">Only an organization admin can view spend and set limits.</Alert>
      )}
    </>
  );
}
